"use client"

import { useState } from "react"
import { Modal, Stack, Text, NumberInput, Button, Alert } from "@forestsync/ui"
import { TbCheck, TbAlertCircle } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { acceptOptionListing } from "@/actions/options"
import type { OptionChainRow } from "@/actions/options"
import { WireInstructionsBlock, type WireInstructions } from "./WireInstructionsBlock"

type Props = {
  opened: boolean
  onClose: () => void
  listing: OptionChainRow | null
  onAccepted?: () => void
}

export function AcceptOptionModal({ opened, onClose, listing, onAccepted }: Props) {
  const t = useTranslations("options")
  const [blocks, setBlocks] = useState(100)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    premiumDueAt: string
    wireInstructions: WireInstructions | null
  } | null>(null)

  if (!listing) return null

  const premiumTotal = (listing.premiumPerTonne * blocks).toFixed(2)

  async function handleAccept() {
    if (!listing) return
    setLoading(true)
    setError(null)
    const res = await acceptOptionListing(listing.refId, blocks, listing.strikePrice, listing.premiumPerTonne)
    setLoading(false)
    if (!("data" in res) || !res.data) {
      setError("error" in res ? (res.error ?? t("acceptFailed")) : t("acceptFailed"))
      return
    }
    setResult({
      premiumDueAt: new Date(res.data.premiumDueAt).toLocaleDateString(),
      wireInstructions: res.data.wireInstructions,
    })
    onAccepted?.()
  }

  function handleClose() {
    setResult(null)
    setError(null)
    setBlocks(100)
    onClose()
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("acceptTitle")} size="md">
      {result ? (
        <Stack gap="md">
          <Alert color="green" icon={<TbCheck size={16} />}>{t("acceptSuccess")}</Alert>
          <Text size="sm">{t("acceptPremiumDue", { amount: premiumTotal, date: result.premiumDueAt })}</Text>
          <WireInstructionsBlock wireInstructions={result.wireInstructions} />
          <Button onClick={handleClose}>{t("done")}</Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">{listing.projectName} · {listing.creditType} · {listing.vintage}</Text>
          <NumberInput
            label={t("acceptBlocks")}
            description={t("acceptBlocksDescription", { max: listing.remainingTonnes })}
            value={blocks}
            onChange={(v) => setBlocks(Number(v) || 0)}
            min={100}
            max={listing.remainingTonnes}
            step={100}
          />
          <Text size="sm">{t("acceptPremiumPreview", { amount: premiumTotal })}</Text>
          {error && <Alert color="red" icon={<TbAlertCircle size={16} />}>{error}</Alert>}
          <Button onClick={handleAccept} loading={loading} disabled={blocks <= 0 || blocks % 100 !== 0 || blocks > listing.remainingTonnes}>
            {t("acceptConfirm")}
          </Button>
        </Stack>
      )}
    </Modal>
  )
}
